import Mustache from 'mustache';
import i18n from '../utils/i18n.js';
import toast from '../utils/toast.js';
import { formatDate, formatVolume, translateDayText } from '../utils/utils.js';
import EsimProvider from '../esim/provider.js';
import config from '../utils/config.js';
import { trackLogin, trackSignUp, trackEsimSuspend, trackEsimUnsuspend } from '../utils/analytics.js';

/**
 * Initializes authentication module
 * @param {Object} account - Account instance
 * @param {Object} sharedDomElements - Shared DOM elements
 * @param {HTMLElement} sharedDomElements.sidebarOverlay - Sidebar overlay element
 * @param {Object} callbacks - Callback functions
 * @param {Function} callbacks.renderCart - Render cart callback
 * @param {Function} callbacks.onLogout - Logout callback
 * @param {Object} callbacks.store - Store instance
 * @param {Function} callbacks.showTopupPlans - Show topup plans callback
 * @returns {Object} Auth module API
 */
export function initAuth(account, sharedDomElements, callbacks) {
    const { sidebarOverlay } = sharedDomElements;
    let { renderCart, onLogout, store, addPackageToCart, showTopupPlans } = callbacks || {};
    const provider = new EsimProvider();
    
    let cachedEsims = [];

    /**
     * Gets "used" bytes for display. API may send orderUsage (used) or dataLeft/data_left (remaining in bytes).
     * When dataLeft is present, used = totalVolume - dataLeft so the display matches the provider.
     * @param {{ totalVolume?: number, orderUsage?: number, dataLeft?: number, data_left?: number }} record
     * @returns {number} Used bytes
     */
    function getUsedBytes(record) {
        if (!record) return 0;
        const total = typeof record.totalVolume === 'number' ? record.totalVolume : 0;
        const dataLeft = record.dataLeft ?? record.data_left;
        if (dataLeft != null && typeof dataLeft === 'number') {
            return Math.max(0, total - dataLeft);
        }
        if (record.orderUsage != null && typeof record.orderUsage === 'number') {
            return record.orderUsage;
        }
        return 0;
    }

    const authSidebar = document.getElementById('authSidebar');
    const loginButton = document.getElementById('loginButton');
    const editProfileMenuButton = document.getElementById('editProfileMenuButton');
    const loginTab = document.getElementById('loginTab');
    const registerTab = document.getElementById('registerTab');
    const logoutButton = document.getElementById('logoutButton');
    const userInfo = document.getElementById('userInfo');
    const loginForm = document.getElementById('loginForm');
    const registerForm = document.getElementById('registerForm');
    const emailVerificationMessage = document.getElementById('emailVerificationMessage');
    const editProfileButton = document.getElementById('editProfileButton');
    const resendVerificationEmailButton = document.getElementById('resendVerificationEmailButton');
    const editProfileSidebar = document.getElementById('editProfileSidebar');
    const editProfileForm = document.getElementById('editProfileForm');
    const editProfileName = document.getElementById('editProfileName');
    const editProfileLastname = document.getElementById('editProfileLastname');
    const editProfileEmail = document.getElementById('editProfileEmail');
    const editProfileLanguage = document.getElementById('editProfileLanguage');
    
    const loginEmail = document.getElementById('loginEmail');
    const loginCode = document.getElementById('loginCode');
    const loginEmailGroup = document.getElementById('loginEmailGroup');
    const loginCodeGroup = document.getElementById('loginCodeGroup');
    const loginSubmitButton = document.getElementById('loginSubmitButton');
    const loginChangeEmailButton = document.getElementById('loginChangeEmailButton');
    const loginChangeEmailGroup = document.getElementById('loginChangeEmailGroup');
    
    const registerName = document.getElementById('registerName');
    const registerLastname = document.getElementById('registerLastname');
    const registerEmail = document.getElementById('registerEmail');
    const registerCode = document.getElementById('registerCode');
    const registerNameGroup = document.getElementById('registerNameGroup');
    const registerLastnameGroup = document.getElementById('registerLastnameGroup');
    const registerEmailGroup = document.getElementById('registerEmailGroup');
    const registerCodeGroup = document.getElementById('registerCodeGroup');
    const registerSubmitButton = document.getElementById('registerSubmitButton');
    
    let loginEmailSent = false;
    let registerEmailSent = false;
    const userEsimsButton = document.getElementById('userEsimsButton');
    const userEsimsSidebar = document.getElementById('userEsimsSidebar');
    const userEsimsSidebarBody = document.getElementById('userEsimsSidebarBody');
    const subtitle = document.querySelector('.subtitle');

    /**
     * Resets login form to initial state
     */
    function resetLoginForm() {
        loginEmailSent = false;
        loginEmailGroup.style.opacity = '1';
        loginEmailGroup.style.pointerEvents = 'auto';
        loginEmail.disabled = false;
        loginCodeGroup.style.opacity = '0.5';
        loginCodeGroup.style.pointerEvents = 'none';
        loginCode.value = '';
        loginCode.disabled = true;
        loginCode.removeAttribute('required');
        if (loginChangeEmailGroup) {
            loginChangeEmailGroup.style.display = 'none';
        }
        loginSubmitButton.setAttribute('data-i18n', 'sendCode');
        i18n.updatePage();
    }

    /**
     * Resets register form to initial state
     */
    function resetRegisterForm() {
        registerEmailSent = false;
        registerNameGroup.classList.remove('hidden');
        registerLastnameGroup.classList.remove('hidden');
        registerEmailGroup.classList.remove('hidden');
        registerCodeGroup.classList.add('hidden');
        registerCode.value = '';
        registerCode.removeAttribute('required');
        registerSubmitButton.setAttribute('data-i18n', 'sendCode');
        i18n.updatePage();
    }

    /**
     * Switches to login tab
     */
    function switchToLoginTab() {
        loginTab.classList.add('active');
        registerTab.classList.remove('active');
        loginForm.classList.add('auth-form-active');
        registerForm.classList.remove('auth-form-active');
        loginForm.reset();
        registerForm.reset();
        resetLoginForm();
        resetRegisterForm();
    }

    /**
     * Switches to register tab
     */
    function switchToRegisterTab() {
        registerTab.classList.add('active');
        loginTab.classList.remove('active');
        registerForm.classList.add('auth-form-active');
        loginForm.classList.remove('auth-form-active');
        loginForm.reset();
        registerForm.reset();
        resetLoginForm();
        resetRegisterForm();
    }

    /**
     * Opens authentication sidebar
     */
    function openAuthSidebarFunc() {
        sidebarOverlay.classList.remove('hidden');
        authSidebar.classList.remove('hidden');
        document.body.style.overflow = 'hidden';
        switchToLoginTab();
    }

    /**
     * Opens register sidebar
     */
    function openRegisterSidebarFunc() {
        sidebarOverlay.classList.remove('hidden');
        authSidebar.classList.remove('hidden');
        document.body.style.overflow = 'hidden';
        switchToRegisterTab();
    }

    /**
     * Closes authentication sidebar
     * @param {boolean} force - Force close even if code was sent
     */
    function closeAuthSidebarFunc(force = false) {
        if (!force && (loginEmailSent || registerEmailSent)) {
            return;
        }
        
        authSidebar.classList.add('hidden');
        const hasOpenSidebar = !editProfileSidebar.classList.contains('hidden') ||
                               !userEsimsSidebar.classList.contains('hidden');
        
        if (!hasOpenSidebar) {
            sidebarOverlay.classList.add('hidden');
            document.body.style.overflow = '';
        }
        loginForm.reset();
        registerForm.reset();
        resetLoginForm();
        resetRegisterForm();
    }

    /**
     * Checks email verification token from URL
     */
    async function checkEmailVerification() {
        const urlParams = new URLSearchParams(window.location.search);
        const verifyToken = urlParams.get('token');
        
        if (verifyToken) {
            window.history.replaceState({}, document.title, window.location.pathname);
            
            try {
                const result = await account.verifyEmail(verifyToken);
                toast.success(i18n.t('emailVerified'));
            } catch (error) {
                toast.error(error.message || i18n.t('emailVerificationFailed'));
            }
        }
    }

    /**
     * Loads user eSIMs from API
     */
    async function loadUserEsims() {
        if (!account.isAuthenticated()) {
            cachedEsims = [];
            return;
        }

        try {
            const user = account.getCurrentUser();
            if (!user || !user.id) {
                throw new Error('User not found');
            }

            const baseUrl = config.api.baseUrl;
            
            const token = account.getToken();
            
            const purchasesResponse = await fetch(`${baseUrl}/user/${user.id}/purchases`, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                }
            });

            if (!purchasesResponse.ok) {
                throw new Error('Failed to load purchases');
            }

            const purchasesData = await purchasesResponse.json();
            
            let purchases = [];
            if (Array.isArray(purchasesData)) {
                purchases = purchasesData;
            } else if (purchasesData && typeof purchasesData === 'object') {
                purchases = purchasesData.purchases || purchasesData.data || purchasesData.items || [];
            }
            
            if (!Array.isArray(purchases) || purchases.length === 0) {
                cachedEsims = [];
                return;
            }

            const orderNos = [...new Set(purchases.map(p => p.esim_order_no).filter(Boolean))];
            
            if (orderNos.length === 0) {
                cachedEsims = [];
                return;
            }

            const esimQueries = await Promise.all(
                orderNos.map(orderNo => provider.query({ 
                    orderNo,
                    pager: { pageNum: 1, pageSize: 100 }
                }))
            );

            const allEsims = [];
            esimQueries.forEach((queryResult, index) => {
                if (queryResult && queryResult.obj && queryResult.obj.esimList) {
                    queryResult.obj.esimList.forEach(esim => {
                        allEsims.push({
                            ...esim,
                            orderNo: orderNos[index],
                            orderUsage: esim.orderUsage || '-',
                            totalVolume: esim.totalVolume || '-'
                        });
                    });
                }
            });

            cachedEsims = allEsims;
        } catch (error) {
            console.error('Error loading eSIMs:', error);
            cachedEsims = [];
        }
    }

    /**
     * Opens user eSIMs sidebar
     */
    async function openUserEsimsSidebarFunc() {
        sidebarOverlay.classList.remove('hidden');
        userEsimsSidebar.classList.remove('hidden');
        document.body.style.overflow = 'hidden';
        
        if (cachedEsims.length > 0) {
            renderEsims(cachedEsims);
        } else {
            userEsimsSidebarBody.innerHTML = `<div class="empty-state rounded-2"><p data-i18n="loadingEsims">Loading your eSIMs...</p></div>`;
            i18n.updatePage();
            
            await loadUserEsims();

            if (cachedEsims.length > 0) {
                renderEsims(cachedEsims);
            } else {
                userEsimsSidebarBody.innerHTML = `<div class="empty-state rounded-2"><p data-i18n="noEsims">No eSIMs found</p></div>`;
                i18n.updatePage();
            }
        }
    }

    /**
     * Closes user eSIMs sidebar
     */
    function closeUserEsimsSidebarFunc() {
        userEsimsSidebar.classList.add('hidden');
        const hasOpenSidebar = !authSidebar.classList.contains('hidden') ||
                               !editProfileSidebar.classList.contains('hidden');
        if (!hasOpenSidebar) {
            sidebarOverlay.classList.add('hidden');
            document.body.style.overflow = '';
        }
    }

    /**
     * Renders eSIM cards
     * @param {Array} esims - Array of eSIM objects
     */
    function renderEsims(esims) {
        if (!esims || esims.length === 0) {
            userEsimsSidebarBody.innerHTML = `<div class="empty-state"><p data-i18n="noEsims">No eSIMs found</p></div>`;
            i18n.updatePage();
            return;
        }

        const template = document.getElementById('template-esim-card');
        const templateHtml = template.innerHTML;
        
        userEsimsSidebarBody.innerHTML = esims.map(esim => {
            let packageName = esim.packageList && esim.packageList.length > 0 
                ? esim.packageList[0].packageName 
                : '-';
            
            if (i18n.getLanguage() === 'es' && packageName !== '-') {
                packageName = packageName
                    .replace(/\bday\b/gi, 'día')
                    .replace(/\bdays\b/gi, 'días');
            }
            
            const totalVolume = esim.totalVolume 
                ? formatVolume(esim.totalVolume) 
                : '-';
            
            const esimStatus = esim.esimStatus || 'pending';
            const smdpStatus = esim.smdpStatus || null;
            
            let combinedStatus = esimStatus;
            let combinedStatusTranslated;
            
            if (esimStatus === 'GOT_RESOURCE') {
                if (smdpStatus !== null) {
                    combinedStatus = smdpStatus;
                    combinedStatusTranslated = i18n.t(smdpStatus) !== smdpStatus 
                        ? i18n.t(smdpStatus) 
                        : smdpStatus;
                } else {
                    combinedStatusTranslated = i18n.t(esimStatus) !== esimStatus 
                        ? i18n.t(esimStatus) 
                        : esimStatus;
                }
            } else if (esimStatus === 'IN_USE') {
                const usedBytes = getUsedBytes(esim);
                if (usedBytes < 500000 && !esim.installationTime) {
                    combinedStatus = 'INSTALLATION';
                    combinedStatusTranslated = i18n.t('INSTALLATION');
                } else {
                    combinedStatus = esimStatus;
                    combinedStatusTranslated = i18n.t(esimStatus) !== esimStatus 
                        ? i18n.t(esimStatus) 
                        : esimStatus;
                }
            } else if (['USED_UP', 'USED_EXPIRED', 'UNUSED_EXPIRED', 'CANCEL', 'REVOKE'].includes(esimStatus)) {
                combinedStatus = esimStatus;
                combinedStatusTranslated = i18n.t(esimStatus) !== esimStatus 
                    ? i18n.t(esimStatus) 
                    : esimStatus;
            } else {
                combinedStatusTranslated = i18n.t(esimStatus) !== esimStatus 
                    ? i18n.t(esimStatus) 
                    : esimStatus;
            }
            
            let daysRemaining = 0;
            let daysText = i18n.t('daysRemaining');
            if (esim.expiredTime) {
                const expiredDate = new Date(esim.expiredTime);
                const now = new Date();
                const diffTime = expiredDate - now;
                const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
                daysRemaining = diffDays > 0 ? diffDays : 0;
                daysText = daysRemaining === 1 ? i18n.t('dayRemaining') : i18n.t('daysRemaining');
            }
            
            let smdpAddress = '';
            let activationCode = '';
            let hasManualData = false;
            if (esim.ac) {
                const acParts = esim.ac.split('$');
                if (acParts.length >= 3) {
                    smdpAddress = acParts[1] || '';
                    activationCode = acParts[2] || '';
                    hasManualData = !!(smdpAddress && activationCode);
                }
            }
            
            let durationUnit = esim.durationUnit || '';
            if (i18n.getLanguage() === 'es' && durationUnit) {
                durationUnit = durationUnit
                    .replace(/\bday\b/gi, 'día')
                    .replace(/\bdays\b/gi, 'días');
            }
            
            const isSuspended = combinedStatus === 'SUSPENDED' || smdpStatus === 'SUSPENDED';
            
            const packageCode = esim.packageList && esim.packageList.length > 0 
                ? esim.packageList[0].packageCode 
                : null;
            
            let showControlButtons = false;
            if (combinedStatus === 'IN_USE' && esim.expiredTime) {
                const expiredDate = new Date(esim.expiredTime);
                const now = new Date();
                showControlButtons = expiredDate > now;
            }
            
            const showEsimActions = !['USED_EXPIRED', 'UNUSED_EXPIRED', 'CANCEL', 'REVOKE'].includes(combinedStatus);
            
            return Mustache.render(templateHtml, {
                packageName: packageName,
                esimStatus: combinedStatus,
                esimStatusTranslated: combinedStatusTranslated,
                esimTranNo: esim.esimTranNo || '',
                orderNo: esim.orderNo || '-',
                totalVolume: totalVolume,
                orderUsage: (() => { const u = getUsedBytes(esim); return u > 0 ? formatVolume(u) : '0'; })(),
                totalDuration: esim.totalDuration || '-',
                durationUnit: durationUnit,
                qrCodeUrl: esim.qrCodeUrl || null,
                ac: esim.ac || null,
                daysRemaining: daysRemaining,
                daysText: daysText,
                hasManualData: hasManualData,
                smdpAddress: smdpAddress,
                activationCode: activationCode,
                apn: esim.apn || null,
                pin: esim.pin || null,
                puk: esim.puk || null,
                isSuspended: isSuspended,
                packageCode: packageCode || '',
                showControlButtons: showControlButtons,
                showEsimActions: showEsimActions
            });
        }).join('');
        
        i18n.updatePage();
        
        userEsimsSidebarBody.querySelectorAll('.esim-qr-code').forEach(img => {
            const qrUrl = img.getAttribute('data-qr-url');
            if (qrUrl) {
                img.src = qrUrl;
            }
        });
        
        userEsimsSidebarBody.querySelectorAll('.esim-view-qr-button').forEach(button => {
            button.addEventListener('click', (e) => {
                const card = button.closest('.esim-card');
                const qrSection = card.querySelector('.esim-qr-section');
                const manualSection = card.querySelector('.esim-manual-section');
                if (qrSection) {
                    qrSection.classList.toggle('hidden');
                }
                if (manualSection) {
                    manualSection.classList.add('hidden');
                }
            });
        });
        
        userEsimsSidebarBody.querySelectorAll('.esim-manual-button').forEach(button => {
            button.addEventListener('click', (e) => {
                const card = button.closest('.esim-card');
                const manualSection = card.querySelector('.esim-manual-section');
                const qrSection = card.querySelector('.esim-qr-section');
                if (manualSection) {
                    manualSection.classList.toggle('hidden');
                }
                if (qrSection) {
                    qrSection.classList.add('hidden');
                }
            });
        });
        
        userEsimsSidebarBody.querySelectorAll('.esim-refresh-button').forEach(button => {
            button.addEventListener('click', async (e) => {
                const esimTranNo = button.getAttribute('data-esim-tran-no');
                if (!esimTranNo) return;
                
                const card = button.closest('.esim-card');
                const dataUsageRow = Array.from(card.querySelectorAll('.esim-info-row')).find(row => {
                    const label = row.querySelector('.esim-label');
                    return label && label.getAttribute('data-i18n') === 'dataUsage';
                });
                const dataUsageValue = dataUsageRow ? dataUsageRow.querySelector('.esim-value') : null;
                
                button.disabled = true;
                button.classList.add('spin');
                
                try {
                    const usageResult = await provider.usage([esimTranNo]);
                    if (usageResult && usageResult.usageList && usageResult.usageList.length > 0) {
                        const usage = usageResult.usageList[0];
                        if (dataUsageValue) {
                            const totalVolume = usage.totalVolume ? formatVolume(usage.totalVolume) : '-';
                            const usedBytes = getUsedBytes(usage);
                            const usedStr = usedBytes > 0 ? formatVolume(usedBytes) : '0';
                            dataUsageValue.textContent = `${usedStr} / ${totalVolume}`;
                        }
                        toast.success(i18n.t('usageUpdated'));
                    }
                } catch (error) {
                    console.error('Error refreshing usage:', error);
                    toast.error(error.message || 'Failed to refresh usage');
                } finally {
                    button.disabled = false;
                    button.classList.remove('spin');
                }
            });
        });
        
        userEsimsSidebarBody.querySelectorAll('.esim-suspend-button').forEach(button => {
            button.addEventListener('click', async (e) => {
                const esimTranNo = button.getAttribute('data-esim-tran-no');
                if (!esimTranNo) return;
                
                button.disabled = true;
                
                try {
                    const result = await provider.suspend({ esimTranNo });
                    if (result && result.code === 0) {
                        trackEsimSuspend(esimTranNo);
                        toast.success(i18n.t('esimSuspended'));
                        await loadUserEsims();
                    } else {
                        toast.error(result?.message || i18n.t('suspendFailed'));
                    }
                } catch (error) {
                    console.error('Error suspending eSIM:', error);
                    toast.error(error.message || i18n.t('suspendFailed'));
                } finally {
                    button.disabled = false;
                }
            });
        });
        
        userEsimsSidebarBody.querySelectorAll('.esim-unsuspend-button').forEach(button => {
            button.addEventListener('click', async (e) => {
                const esimTranNo = button.getAttribute('data-esim-tran-no');
                if (!esimTranNo) return;
                
                button.disabled = true;
                
                try {
                    const result = await provider.unsuspend({ esimTranNo });
                    if (result && result.code === 0) {
                        trackEsimUnsuspend(esimTranNo);
                        toast.success(i18n.t('esimUnsuspended'));
                        await loadUserEsims();
                    } else {
                        toast.error(result?.message || i18n.t('unsuspendFailed'));
                    }
                } catch (error) {
                    console.error('Error unsuspending eSIM:', error);
                    toast.error(error.message || i18n.t('unsuspendFailed'));
                } finally {
                    button.disabled = false;
                }
            });
        });
        
        userEsimsSidebarBody.querySelectorAll('.esim-topup-button').forEach(button => {
            button.addEventListener('click', async (e) => {
                const esimTranNo = button.getAttribute('data-esim-tran-no');
                const packageCode = button.getAttribute('data-package-code');
                
                if (!packageCode) {
                    toast.error(i18n.t('packageCodeNotFound'));
                    return;
                }

                button.disabled = true;
                const originalText = button.textContent;
                button.textContent = i18n.t('loading');

                try {
                    const result = await provider.packages({
                        packageCode: packageCode,
                        type: 'TOPUP'
                    });

                    if (result && result.obj && result.obj.packageList && result.obj.packageList.length > 0) {
                        showTopupPlans(result.obj.packageList, esimTranNo);
                    } else {
                        toast.error(i18n.t('noTopupPlansAvailable'));
                    }
                } catch (error) {
                    console.error('Error loading topup plans:', error);
                    toast.error(error.message || i18n.t('errorLoadingTopupPlans'));
                } finally {
                    button.disabled = false;
                    button.textContent = originalText;
                    i18n.updatePage();
                }
            });
        });
    }


    /**
     * Updates authentication UI based on user state
     */
    function updateAuthUI() {
        if (account.isAuthenticated()) {
            const user = account.getCurrentUser();
            
            if (user && user.language) {
                i18n.setLanguage(user.language);
                if (window.updateLanguageButtons) {
                    window.updateLanguageButtons();
                }
            }
            
            loginButton.classList.add('hidden');
            editProfileMenuButton.classList.remove('hidden');
            logoutButton.classList.remove('hidden');
            userEsimsButton.classList.remove('hidden');
            userInfo.textContent = i18n.t('hello', { name: user.name });
            userInfo.classList.remove('hidden');
            
            if (subtitle && user.name) {
                const subtitleText = i18n.t('subtitle');
                subtitle.textContent = `${user.name}, ${subtitleText}`;
            }
            
            emailVerificationMessage.classList.add('hidden');
            
            loadUserEsims();
        } else {
            loginButton.classList.remove('hidden');
            editProfileMenuButton.classList.add('hidden');
            logoutButton.classList.add('hidden');
            userEsimsButton.classList.add('hidden');
            userInfo.classList.add('hidden');
            emailVerificationMessage.classList.add('hidden');
            
            if (subtitle) {
                subtitle.setAttribute('data-i18n', 'subtitle');
                i18n.updatePage();
            }
        }
    }

    /**
     * Opens edit profile sidebar
     */
    function openEditProfileSidebarFunc() {
        const user = account.getCurrentUser();
        if (user) {
            editProfileName.value = user.name || '';
            editProfileLastname.value = user.lastname || '';
            editProfileEmail.value = user.email || '';
            editProfileLanguage.value = user.language || i18n.getLanguage();
        }
        sidebarOverlay.classList.remove('hidden');
        editProfileSidebar.classList.remove('hidden');
        document.body.style.overflow = 'hidden';
    }

    /**
     * Closes edit profile sidebar
     */
    function closeEditProfileSidebarFunc() {
        editProfileSidebar.classList.add('hidden');
        const hasOpenSidebar = !authSidebar.classList.contains('hidden') ||
                               !userEsimsSidebar.classList.contains('hidden');
        if (!hasOpenSidebar) {
            sidebarOverlay.classList.add('hidden');
            document.body.style.overflow = '';
        }
        editProfileForm.reset();
    }

    /**
     * Sets up event listeners
     */
    function setupEventListeners() {
        if (editProfileButton) {
            editProfileButton.addEventListener('click', openEditProfileSidebarFunc);
        }
        if (userEsimsButton) {
            userEsimsButton.addEventListener('click', openUserEsimsSidebarFunc);
        }
        
        resendVerificationEmailButton.addEventListener('click', async () => {
            try {
                resendVerificationEmailButton.disabled = true;
                await account.resendVerificationEmail();
                toast.success(i18n.t('verificationEmailResent'));
            } catch (error) {
                toast.error(error.message || i18n.t('verificationEmailResendFailed'));
            } finally {
                resendVerificationEmailButton.disabled = false;
            }
        });
        
        loginTab.addEventListener('click', (e) => {
            e.preventDefault();
            switchToLoginTab();
        });
        
        registerTab.addEventListener('click', (e) => {
            e.preventDefault();
            switchToRegisterTab();
        });
        
        if (loginButton) {
            loginButton.addEventListener('click', () => {
                openAuthSidebarFunc();
            });
        }
        
        if (editProfileMenuButton) {
            editProfileMenuButton.addEventListener('click', () => {
                openEditProfileSidebarFunc();
            });
        }

        loginForm.addEventListener('submit', async (e) => {
            e.preventDefault();
            
            const email = loginEmail.value.trim();
            
            if (!loginEmailSent) {
                try {
                    await account.sendVerificationCode(email);
                    loginEmailSent = true;
                    loginEmailGroup.style.opacity = '0.5';
                    loginEmailGroup.style.pointerEvents = 'none';
                    loginEmail.disabled = true;
                    loginCodeGroup.style.opacity = '1';
                    loginCodeGroup.style.pointerEvents = 'auto';
                    loginCode.disabled = false;
                    loginCode.setAttribute('required', 'required');
                    if (loginChangeEmailGroup) {
                        loginChangeEmailGroup.style.display = 'flex';
                    }
                    loginSubmitButton.setAttribute('data-i18n', 'verifyCode');
                    i18n.updatePage();
                    toast.success(i18n.t('codeSent'));
                } catch (error) {
                    toast.error(error.message || i18n.t('codeSendFailed'));
                }
            } else {
                const code = loginCode.value.trim();
                if (!code || code.length !== 6) {
                    toast.error(i18n.t('invalidCode'));
                    return;
                }
                
                try {
                    await account.verifyCode(email, code);
                    const user = account.getCurrentUser();
                    if (user && user.language) {
                        i18n.setLanguage(user.language);
                        if (window.updateLanguageButtons) {
                            window.updateLanguageButtons();
                        }
                    }
                    loginEmailSent = false;
                    trackLogin();
                    closeAuthSidebarFunc(true);
                    updateAuthUI();
                    if (renderCart) renderCart();
                    toast.success(i18n.t('loginSuccessful'));
                } catch (error) {
                    toast.error(error.message || i18n.t('codeVerificationFailed'));
                }
            }
        });

        registerForm.addEventListener('submit', async (e) => {
            e.preventDefault();
            
            const name = registerName.value.trim();
            const lastname = registerLastname.value.trim();
            const email = registerEmail.value.trim();
            
            if (!registerEmailSent) {
                if (!name || !lastname) {
                    toast.error(i18n.t('nameRequired'));
                    return;
                }
                
                const fullName = `${name} ${lastname}`;
                
                try {
                    await account.sendVerificationCode(email, fullName);
                    registerEmailSent = true;
                    registerNameGroup.classList.add('hidden');
                    registerLastnameGroup.classList.add('hidden');
                    registerEmailGroup.classList.add('hidden');
                    registerCodeGroup.classList.remove('hidden');
                    registerCode.setAttribute('required', 'required');
                    registerSubmitButton.setAttribute('data-i18n', 'verifyCode');
                    i18n.updatePage();
                    toast.success(i18n.t('codeSent'));
                } catch (error) {
                    toast.error(error.message || i18n.t('codeSendFailed'));
                }
            } else {
                const code = registerCode.value.trim();
                if (!code || code.length !== 6) {
                    toast.error(i18n.t('invalidCode'));
                    return;
                }
                
                try {
                    await account.verifyCode(email, code);
                    const user = account.getCurrentUser();
                    if (user && user.language) {
                        i18n.setLanguage(user.language);
                        if (window.updateLanguageButtons) {
                            window.updateLanguageButtons();
                        }
                    }
                    registerEmailSent = false;
                    trackSignUp();
                    toast.success(i18n.t('registrationSuccessful'));
                    registerForm.reset();
                    updateAuthUI();
                    if (renderCart) renderCart();
                    setTimeout(() => closeAuthSidebarFunc(true), 2000);
                } catch (error) {
                    toast.error(error.message || i18n.t('codeVerificationFailed'));
                }
            }
        });

        logoutButton.addEventListener('click', () => {
            account.logout();
            updateAuthUI();
            if (onLogout) onLogout();
        });

        if (loginChangeEmailButton) {
            loginChangeEmailButton.addEventListener('click', () => {
                resetLoginForm();
            });
        }

        editProfileForm.addEventListener('submit', async (e) => {
            e.preventDefault();
            
            const user = account.getCurrentUser();
            if (!user || !user.id) {
                toast.error(i18n.t('profileUpdateFailed'));
                return;
            }

            const name = editProfileName.value.trim();
            const lastname = editProfileLastname.value.trim();
            const email = editProfileEmail.value.trim();
            const language = editProfileLanguage.value;

            if (!name || !lastname) {
                toast.error(i18n.t('nameRequired'));
                return;
            }

            if (!email) {
                toast.error(i18n.t('emailRequired'));
                return;
            }

            const profileData = { name, lastname, email, language };

            try {
                await account.updateProfile(user.id, profileData);
                toast.success(i18n.t('profileUpdated'));
                updateAuthUI();
                closeEditProfileSidebarFunc();
            } catch (error) {
                toast.error(error.message || i18n.t('profileUpdateFailed'));
            }
        });
    }

    setupEventListeners();
    checkEmailVerification();
    updateAuthUI();

    /**
     * Checks if any sidebar is open
     * @returns {boolean} True if any sidebar is open
     */
    function hasOpenSidebars() {
        return !authSidebar.classList.contains('hidden') ||
               !editProfileSidebar.classList.contains('hidden') ||
               !userEsimsSidebar.classList.contains('hidden');
    }

    function closeAnyAuthSidebar() {
        if (loginEmailSent || registerEmailSent) {
            return;
        }
        
        if (!authSidebar.classList.contains('hidden')) {
            closeAuthSidebarFunc();
        } else if (!editProfileSidebar.classList.contains('hidden')) {
            closeEditProfileSidebarFunc();
        } else if (!userEsimsSidebar.classList.contains('hidden')) {
            closeUserEsimsSidebarFunc();
        }
    }

    /**
     * Sets callback functions
     * @param {Object} newCallbacks - New callback functions
     */
    function setCallbacks(newCallbacks) {
        if (newCallbacks.renderCart) renderCart = newCallbacks.renderCart;
        if (newCallbacks.onLogout) onLogout = newCallbacks.onLogout;
        if (newCallbacks.store) store = newCallbacks.store;
        if (newCallbacks.showTopupPlans) showTopupPlans = newCallbacks.showTopupPlans;
    }

    return {
        openAuthSidebarFunc,
        openRegisterSidebarFunc,
        closeAuthSidebarFunc,
        openEditProfileSidebarFunc,
        closeEditProfileSidebarFunc,
        closeUserEsimsSidebarFunc,
        updateAuthUI,
        checkEmailVerification,
        hasOpenSidebars,
        closeAnyAuthSidebar,
        setCallbacks
    };
}

